// Plain-English ladder challenge settings, shared by the admin settings summary
// and the public rules list (audit A8, Grant 2026-09-21):
//  - "above" (maxRankingDifference) and "below" (maxRankingDifferenceBelow) are
//    independent; each only limits challenges in its own direction.
//  - 0 never means "unlimited". Below 0 = no downward challenges. Above has a
//    minimum of 1 (legacy 0 rows are data-fixed; if one still arrives, say so).
//  - Expiry has a minimum of 1 day; a legacy 0 voids challenges straight away.
//  - The top-players limit only applies to challenges UP the ladder.
// Unit tested in tests/unit/ladder-challenge-summary.test.ts.

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/** Missing / blank → null; otherwise the integer. */
const num = (v) => {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? Math.trunc(n) : null;
};

/** "Up to 2 above · No challenges below" */
export function challengeRangeText(above, below) {
    const a = num(above);
    const b = num(below);
    const up = a == null ? null : (a <= 0 ? 'No challenges above' : `Up to ${a} above`);
    const down = b == null || b <= 0 ? 'No challenges below' : `up to ${b} below`;
    return up ? `${up} · ${down}` : down.charAt(0).toUpperCase() + down.slice(1);
}

/** Below-limit value on its own: 0 → "No challenges below". */
export function belowLimitText(below) {
    const b = num(below);
    return b == null || b <= 0 ? 'No challenges below' : `Up to ${b} below`;
}

/** Above-limit value on its own: legacy 0 → "No challenges above" (never "Anyone"). */
export function aboveLimitText(above) {
    const a = num(above);
    return a == null || a <= 0 ? 'No challenges above' : `Up to ${a} above`;
}

/** {v, s} for the challenge-expiry summary row. */
export function expiryText(days) {
    const d = num(days);
    if (d == null || d <= 0) {
        return {v: 'Void straight away', s: 'an expiry of 0 days voids every challenge, so set at least 1 day'};
    }
    return {v: `${plural(d, 'day', 'days')} to play`, s: 'unplayed challenges become void'};
}

/** Top-players rule: applies to upward challenges only. */
export function topPlayersText(topCount, maxDifference) {
    const t = num(topCount);
    if (!t || t <= 0) return 'No special rule';
    const m = num(maxDifference) ?? 0;
    return m <= 0
        ? `Top ${t} can't challenge anyone above them`
        : `Top ${t} can challenge up to ${m} above`;
}

/**
 * A number field value for the API: blank / non-numeric → null (so the form
 * says "Enter a number" instead of sending "" and getting a 500).
 */
export function toIntOrNull(v) {
    if (v === null || v === undefined) return null;
    if (typeof v === 'string' && v.trim() === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? Math.trunc(n) : null;
}
